import {
    useForm,
    type SubmitHandler,
} from "react-hook-form"
import Alert from "@mui/material/Alert"

import useFormLocalStorage from "@/hooks/use-form-local-storage"
import { APP_VERSION } from "@/constants/general"
import Form from "@/components/forms/reusables/form"
import useFormStates from "@/hooks/use-form-states"
import type {
    HouseholdMemberCustomDiscountFormInput,
} from "@/types"
import RHFNumberTextfield from "@/components/form-fields/rhf-number-textfield"
import RHFTextfield from "@/components/form-fields/rhf-textfield"
import { useCreateHouseholdCustomDiscount, useCreateHouseholdMemberCustomDiscount } from "@/hooks/api/use-yearly-fee-payers"


const HouseholdMemberCustomDiscountForm = ({
    feePayerId,
    closeCallback,
    type,
} : {
    feePayerId: number | string,
    closeCallback?: () => void,
    type: 'household' | 'household_member',
}) => {

    const formDataId = `householdMemberCustomDiscountForm-${feePayerId}-v${APP_VERSION}`

    const initialValues = {
        name: '',
        description: '',
        amount: '',
    }
    
    const {
        control,
        handleSubmit,
        formState: { isDirty },
        reset,
    } = useForm({
        defaultValues: { ...initialValues },
    })

    const [isLoadedFromLocalStorage, handleFormReset] = useFormLocalStorage({
        control,
        formDataId,
        reset,
        initialValues
    })

    const {
        resetWithVersion,
        requestInProgress,
        setRequestInProgress,
        setError,
        error,
    } = useFormStates(handleFormReset)

    const mutationCreateHouseholdMember = useCreateHouseholdMemberCustomDiscount(
        feePayerId,
        {
            setRequestInProgress,
            formDataId,
            onSuccessCallback: closeCallback,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            }
        },
    )

    const mutationCreateHousehold = useCreateHouseholdCustomDiscount(
        feePayerId,
        {
            setRequestInProgress,
            formDataId,
            onSuccessCallback: closeCallback,
            onErrorCallback(errorMessage) {
                setError(errorMessage)
            }
        },
    )

    const onSubmit: SubmitHandler<HouseholdMemberCustomDiscountFormInput> = data => {
        setRequestInProgress(true)
        if (type=== 'household') {
            mutationCreateHousehold.mutate(data)
        } else {
            mutationCreateHouseholdMember.mutate(data)
        }
    }

    return (
        <Form
            onSubmit={handleSubmit(onSubmit)}
            heading={'Vytvořit slevu na míru'}
            closeCallback={closeCallback}
            isLoadedFromLocalStorage={isLoadedFromLocalStorage}
            isDirty={isDirty}
            resetWithVersion={resetWithVersion}
            requestInProgress={requestInProgress}
            buttonText={'Vytvořit slevu'}
            error={error}
            setError={setError}
        >
            {type === 'household' &&
                <Alert
                    severity="warning"
                    sx={(theme) => ({
                        flexGrow: 1,
                        margin: theme.spacing(1, 0),
                    })}
                >
                    Sleva bude po vytvoření přiřazena všem členům svozového místa
                </Alert>
            }
            <RHFTextfield
                name="name"
                control={control}
                label="Název slevy"
            />
            <RHFNumberTextfield
                name="amount"
                control={control}
                label="Výše slevy"
                endAdornment="Kč"
                decimalScale={2}
                thousandSeparator
                disallowNegative
            />
            <RHFTextfield
                name="description"
                control={control}
                label="Popis slevy"
            />
        </Form>
    )
}

export default HouseholdMemberCustomDiscountForm
